import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { useZohoOrgId, zohoPaymentUrl } from '../../../utils/zohoOrg';
import './wallet.css';

// Admin "Payment issues" area (PLAN_payments.md phase 6) — rendered in
// PendingDeposits beside Pending Payouts / Disputed Adjustments.
//
//   * Payment-due queue: saved-card auto-charges that failed after the retry
//     (booking_payment_charges.status = payment_due / retry_scheduled). Row =
//     booking · client · amount · attempts · next retry. Actions: Retry now
//     (re-attempt the MIT charge) / Resolve manually (mark handled, with a note).
//   * Reconciliation mini-panel: gateway-vs-ledger summary counts + orphan lists.
//
// Every call is best-effort (the endpoints belong to the parallel auto-charge
// engine and may not be deployed yet) — a 404/500 shows an empty state or hides
// the panel rather than breaking the Pending Deposits page.
//
// Backend contract (routes/payments.js admin, phase-6):
//   GET  /api/payments/admin/payment-due        -> { charges: [ { id, booking_id,
//        client_id, client_name, amount, attempts, next_retry_at, status,
//        scheme, masked_pan, created_at } ] }
//   POST /api/payments/admin/charges/:id/retry
//   POST /api/payments/admin/charges/:id/mark-paid  { note }
//   GET  /api/payments/admin/reconciliation     -> the EXACT shape below
//
// Reconciliation contract (routes/payments.js:2843, response built at 2926) —
// bound literally, NOT guessed at. The previous "generic" panel read
// `recon.summary` (never sent) and treated `recon.orphans` as a {label: rows}
// map when it is an ARRAY of rows, so it returned null for every possible
// input and the entire operator view of stranded money was invisible:
//   { days,
//     gateway_checkouts:        [ { status, count, total } ],
//     orphans:                  [ { gateway_order_ref, deposit_id, amount,
//                                   client_id, deposit_status } ],
//     deposits_without_checkout:[ { id, gateway_order_ref, amount, status,
//                                   client_id } ],
//     stranded_captures:        [ { gateway_order_ref, kind, amount, client_id,
//                                   status, resolved_at, gateway_state } ],
//     unapplied_settlements:    [ { id, client_id, source, status, zoho_amount,
//                                   zoho_payment_id, applied_amount,
//                                   gateway_order_ref, deposit_id, created_at } ],
//     unknown_gateway_deposits: [ { id, client_id, amount, status,
//                                   gateway_order_ref, gateway_unknown_at, notes,
//                                   client_name } ],
//     counts: { orphans, deposits_without_checkout, stranded_captures,
//               unapplied_settlements, unknown_gateway_deposits },
//     stranded_captures_partial, stranded_captures_probed,
//     stranded_captures_total, gateway_mode }
//
// Resolution endpoints — THESE ARE THE ONLY ADMIN POSTS THAT EXIST. Verified by
// enumerating every router.post('/admin… in routes/payments.js. An earlier pass
// invented /checkouts/:ref/resettle, /stranded/:ref/mark-handled and
// /settlements/:id/reapply; all three 404 on deploy, so three of the four
// reconciliation actions did nothing at all. Do not add a button here without
// grepping the router for the route AND its required body first.
//   POST /api/payments/admin/settlements/:id/retry
//        (no body; refuses unless the money is provably taken)
//   POST /api/payments/admin/settlements/:id/resolve
//        { resolution: 'zoho_applied_manually'|'refunded'|'written_off',
//          note (REQUIRED, non-empty),
//          zoho_payment_id (REQUIRED when resolution === 'zoho_applied_manually') }
//   POST /api/payments/admin/checkouts/:ref/mark-refunded
//        { note (REQUIRED), refund_reference (REQUIRED) }
// There is NO endpoint that re-credits an orphan or a stray deposit, so those
// buckets are read-only here and say so rather than offering a dead button.
export default function PaymentDueSection({ onChanged, onCountChange, onReconSummary }) {
    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recon, setRecon] = useState(null);
    const [action, setAction] = useState(null); // { type: 'retry'|'resolve', charge }
    const [note, setNote] = useState('');
    const [processing, setProcessing] = useState(false);

    // The UI gate must mirror authorizePaymentsStaff (routes/payments.js:31)
    // EXACTLY — admin + manager + supervisor. Gating on 'manager' alone hid the
    // Retry / Resolve buttons from admins and supervisors the backend already
    // authorises, so a real payment-due backlog could not be actioned unless a
    // manager happened to be available. One constant, used by every action
    // surface in this file, so the two gates cannot drift apart again.
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const canActOnPayments = ['manager', 'admin', 'supervisor'].includes(userRole);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (d) => (d ? DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm') : '—');

    const fetchCharges = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/payments/admin/payment-due');
            setCharges(Array.isArray(res.data) ? res.data : (res.data?.charges || []));
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error fetching payment-due queue:', err);
            setCharges([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRecon = useCallback(async () => {
        try {
            const res = await axios.get('/api/payments/admin/reconciliation');
            setRecon(res.data && typeof res.data === 'object' ? res.data : null);
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error fetching reconciliation:', err);
            setRecon(null);
        }
    }, []);

    useEffect(() => {
        fetchCharges();
        fetchRecon();
    }, [fetchCharges, fetchRecon]);

    const refresh = async () => {
        await Promise.all([fetchCharges(), fetchRecon()]);
        if (onChanged) onChanged();
    };

    const closeModal = () => { setAction(null); setNote(''); };

    const handleRetry = async () => {
        if (!action) return;
        setProcessing(true);
        try {
            await axios.post(`/api/payments/admin/charges/${action.charge.id}/retry`);
            toast.success('Retry queued — the saved card will be charged again.');
            closeModal();
            await refresh();
        } catch (err) {
            console.error('Error retrying charge:', err);
            toast.error(err.response?.data?.error || 'Failed to retry the charge.');
        } finally {
            setProcessing(false);
        }
    };

    const handleResolve = async () => {
        if (!action) return;
        if (!note.trim()) {
            toast.error('Add a note describing how this was resolved.');
            return;
        }
        setProcessing(true);
        try {
            await axios.post(`/api/payments/admin/charges/${action.charge.id}/mark-paid`, { note: note.trim() });
            toast.success('Marked resolved.');
            closeModal();
            await refresh();
        } catch (err) {
            console.error('Error resolving charge:', err);
            toast.error(err.response?.data?.error || 'Failed to resolve the charge.');
        } finally {
            setProcessing(false);
        }
    };

    const reconIssues = reconIssueCount(recon);

    // Report up to the page (owner, 2026-09-07): the failed-charge count and
    // the gateway's 7-day summary feed the overview cards, and the page hides
    // this whole section when there is nothing here to work.
    useEffect(() => {
        if (!loading && onCountChange) onCountChange(charges.length);
    }, [loading, charges.length, onCountChange]);
    useEffect(() => {
        if (!onReconSummary) return;
        onReconSummary(recon ? {
            days: recon.days || 7,
            issues: reconIssueCount(recon),
            checkouts: Array.isArray(recon.gateway_checkouts) ? recon.gateway_checkouts : [],
        } : null);
    }, [recon, onReconSummary]);

    const cardLabel = (c) => {
        if (!c.scheme && !c.masked_pan) return '—';
        const last4 = String(c.masked_pan || '').replace(/\D/g, '').slice(-4);
        return `${c.scheme || 'Card'}${last4 ? ` ••${last4}` : ''}`;
    };

    // Nothing failed and nothing to reconcile: the section is not rendered at
    // all — the overview row says "nothing needs attention" instead.
    if (!loading && charges.length === 0 && !reconPanelVisible(recon)) return null;

    return (
        <div className="swallet__section">
            <div className="swallet__sectionHead">
                <h2 className="swallet__sectionTitle">Payment Issues</h2>
                {/* Unmissable count of stranded/uncredited money. Without it an
                    operator reads "No payments due." and concludes the system is
                    clean while captured client money sits below. */}
                {reconIssues > 0 && (
                    <span className="swallet__reconBadge">
                        {reconIssues} needing attention
                    </span>
                )}
                <span className="swallet__sectionSub">
                    Saved-card auto-charges that failed. Retry the card, or resolve manually with a note.
                </span>
            </div>

            {loading ? (
                <div className="swallet__empty">Loading...</div>
            ) : charges.length === 0 ? null : (
                <table className="swallet__table">
                    <thead>
                        <tr>
                            <th>Booking</th>
                            <th>Client</th>
                            <th>Card</th>
                            <th>Amount</th>
                            <th>Attempts</th>
                            <th>Next Retry</th>
                            <th>Status</th>
                            {canActOnPayments && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {charges.map((c) => (
                            <tr key={c.id}>
                                <td>
                                    <Link to={`/bookings/${c.booking_id}`} className="swallet__proofLink">
                                        #{c.booking_id}
                                    </Link>
                                </td>
                                <td style={{ fontWeight: 900 }}>
                                    {c.client_id ? (
                                        <Link to={`/wallet/clients/${c.client_id}`} className="swallet__proofLink">
                                            {c.client_name || `Client #${c.client_id}`}
                                        </Link>
                                    ) : (c.client_name || '—')}
                                </td>
                                <td>{cardLabel(c)}</td>
                                <td style={{ fontWeight: 900 }}>{money(c.amount)}</td>
                                <td>{c.attempts ?? 0}</td>
                                <td>{formatDate(c.next_retry_at)}</td>
                                <td>
                                    <span className={`swallet__chip ${c.status === 'payment_due' ? 'swallet__chip--red' : 'swallet__chip--orange'}`}>
                                        {c.status === 'payment_due' ? 'Payment due' : c.status === 'retry_scheduled' ? 'Retry scheduled' : (c.status || '—')}
                                    </span>
                                </td>
                                {canActOnPayments && (
                                    <td>
                                        <div className="swallet__btnGroup">
                                            <button
                                                className="swallet__btn swallet__btn--success"
                                                onClick={() => setAction({ type: 'retry', charge: c })}
                                            >
                                                Retry now
                                            </button>
                                            <button
                                                className="swallet__btn"
                                                onClick={() => setAction({ type: 'resolve', charge: c })}
                                            >
                                                Resolve manually
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <ReconciliationPanel
                recon={recon}
                money={money}
                formatDate={formatDate}
                canAct={canActOnPayments}
                onResolved={refresh}
            />

            {action && (
                <div className="swallet__overlay" onClick={(e) => { if (e.target === e.currentTarget && !processing) closeModal(); }}>
                    <div className="swallet__modal">
                        {action.type === 'retry' && (
                            <>
                                <h3 className="swallet__modalTitle">Retry Card Charge</h3>
                                <p className="swallet__modalText">
                                    Re-attempt the auto-charge of <strong>{money(action.charge.amount)}</strong> for Booking{' '}
                                    <strong>#{action.charge.booking_id}</strong> ({action.charge.client_name || `Client #${action.charge.client_id}`})
                                    on the saved card now?
                                </p>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>Cancel</button>
                                    <button className="swallet__btn swallet__btn--success" onClick={handleRetry} disabled={processing}>
                                        {processing ? 'Processing...' : 'Retry now'}
                                    </button>
                                </div>
                            </>
                        )}
                        {action.type === 'resolve' && (
                            <>
                                <h3 className="swallet__modalTitle">Resolve Manually</h3>
                                <p className="swallet__modalText">
                                    Mark the <strong>{money(action.charge.amount)}</strong> charge for Booking{' '}
                                    <strong>#{action.charge.booking_id}</strong> as resolved (e.g. paid another way, waived,
                                    or handled by bank transfer). This stops further retries.
                                </p>
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">How was it resolved? (required)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="e.g. Client paid by bank transfer, ref 12345"
                                    />
                                </div>
                                <div className="swallet__modalActions">
                                    <button className="swallet__btn" onClick={closeModal} disabled={processing}>Cancel</button>
                                    <button className="swallet__btn swallet__btn--primary" onClick={handleResolve} disabled={processing || !note.trim()}>
                                        {processing ? 'Processing...' : 'Mark resolved'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Humanise a snake_case backend key: "stranded_captures" -> "Stranded captures".
function humanize(key) {
    const s = String(key).replace(/_/g, ' ' ).trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Reconciliation panel (W5)
// ---------------------------------------------------------------------------
// Bound LITERALLY to GET /api/payments/admin/reconciliation. The old panel was
// written against an imagined generic contract ({summary}, {orphans: {label:
// rows}}) that the backend has never sent, so `return null` fired for every
// possible input and captured-but-uncredited client money was invisible on the
// only reconciliation surface in the product. Never re-generalise this: if the
// backend contract changes, change THIS list to match it.
//
// Every bucket below is money that may have left the client's card without
// reaching the ledger, so the rule is: if a list is non-empty it MUST render.

// EVERY bucket the backend returns, in the order an operator should work them:
// worst (money taken, definitely not credited) first. This list must stay a
// superset of the arrays in the /admin/reconciliation response — a bucket that
// is missing here is money that is invisible AND uncounted, which is exactly
// how unknown_gateway_deposits (migration 10) shipped unrendered while its
// whole reason for existing was that those deposits used to vanish.
const ORPHAN_BUCKETS = [
    {
        key: 'orphans',
        label: 'Captured but not credited (orphans)',
        blurb: 'The gateway marked the checkout paid but the deposit never reached "approved". Real money captured and not credited.',
        action: 'checkout',
    },
    {
        key: 'stranded_captures',
        label: 'Stranded captures (checkout torn down, money taken)',
        blurb: 'The checkout was cancelled/expired/failed on our side, but the gateway still holds or captured the money.',
        action: 'checkout',
    },
    // ── DIRECT CARD RAIL buckets (2026-07-18) ───────────────────────────────
    // Both of these are returned by GET /admin/reconciliation and NEITHER was
    // listed here, so they were invisible AND uncounted — the badge read "all
    // clear" over them. They are the direct-rail analogue of `orphans`: on the
    // old rail a stranded capture always left a wallet_deposit to find, and on
    // the direct rail it leaves a receipt (or nothing at all) instead.
    {
        key: 'receipts_without_capture',
        label: 'Card receipts with no traceable capture',
        blurb: 'A direct-rail receipt records money taken from a card, but its gateway reference matches no checkout, auto-charge or capture attempt. The payment record exists with nothing explaining where the money came from — reconcile it against the gateway.',
        action: null,
    },
    {
        key: 'unknown_capture_attempts',
        label: 'Card capture attempts with an unresolved outcome',
        blurb: 'We sent a capture to the gateway and never learned whether it succeeded. These hold NO credit but may correspond to real money taken — check the reference at the gateway and either credit or refund it there.',
        action: null,
    },
    {
        key: 'unapplied_settlements',
        label: 'Unapplied settlements (money taken, invoices unpaid)',
        blurb: 'A settlement took the money but never reached the Zoho invoices.',
        action: 'settlement',
    },
    {
        key: 'unknown_gateway_deposits',
        label: 'Deposits with an unresolved gateway outcome',
        blurb: 'The admin card rail lost the gateway response after sending the charge. These hold NO credit but may correspond to a real capture — check the gateway for the reference and either credit or refund it there.',
        action: null,
    },
    {
        key: 'deposits_without_checkout',
        label: 'Deposits with no checkout',
        blurb: 'A deposit carries a gateway reference with no traceable checkout, auto-charge or receipt. Investigate manually.',
        action: null,
    },
];

// Total number of rows needing attention, across every bucket. Drives the red
// badge on the section header so a non-empty queue cannot be walked past.
//
// Prefers the backend's own `counts` per bucket (it is authoritative and is
// capped/limited identically to the arrays), falling back to the array length
// for a pre-counts backend. A bucket the backend counts but does not list still
// contributes to the badge — an operator must never read "all clear" over money
// the server knows about.
export function reconIssueCount(recon) {
    if (!recon) return 0;
    return ORPHAN_BUCKETS.reduce((n, b) => {
        const counted = recon.counts && Number(recon.counts[b.key]);
        if (Number.isFinite(counted)) return n + counted;
        const rows = recon[b.key];
        return n + (Array.isArray(rows) ? rows.length : 0);
    }, 0);
}

// The exact enum routes/payments.js:3250 validates. Anything else is a 400.
const RESOLUTIONS = [
    {
        value: 'zoho_applied_manually',
        label: 'Zoho payment created manually',
        hint: 'You created the payment in Zoho yourself. The settlement will read as SETTLED. The Zoho payment id is required.',
    },
    {
        value: 'refunded',
        label: 'Refunded to the client',
        hint: 'The money went back to the client, so nothing should be applied to the invoices. The settlement will read as WRITTEN OFF.',
    },
    {
        value: 'written_off',
        label: 'Written off',
        hint: 'Neither applied nor refunded — closed deliberately. The invoices stay unpaid.',
    },
];

// Status chips get a colour by severity so 'failed' does not read like 'paid'.
function statusChipClass(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'paid' || s === 'settled' || s === 'approved') return 'swallet__chip--green';
    if (s === 'failed' || s === 'expired') return 'swallet__chip--red';
    if (s === 'pending' || s === 'applying' || s === 'created') return 'swallet__chip--orange';
    return 'swallet__chip--blue';
}

// Does the reconciliation panel have anything a person must WORK — orphan
// rows, or a "we could not check the gateway" warning? Shared by the panel
// (renders null otherwise) and its parent section (hides itself when neither
// it nor the panel has anything). The paid/failed/expired summary is not
// work; it shows on the overview cards.
function reconPanelVisible(recon) {
    if (!recon) return false;
    const anyRows = ORPHAN_BUCKETS.some((b) => Array.isArray(recon[b.key]) && recon[b.key].length > 0);
    const probeTotal = Number(recon.stranded_captures_total);
    const probePartial = recon.stranded_captures_partial === true;
    const gatewayBlind = recon.gateway_mode === 'disabled' && Number.isFinite(probeTotal) && probeTotal > 0;
    return anyRows || probePartial || gatewayBlind;
}

function ReconciliationPanel({ recon, money, formatDate, canAct, onResolved }) {
    // Persistent per-row outcomes. Deliberately NOT optimistic: a row only ever
    // shows what the server actually answered, and the note survives the
    // refresh that follows, so the operator can see which rows they have
    // already actioned and what happened.
    const [outcomes, setOutcomes] = useState({});
    const [busyKey, setBusyKey] = useState(null);
    // Live vs sandbox org for the Zoho payment links (null until fetched).
    const zohoOrgId = useZohoOrgId();
    // Multi-field resolution form. Both surviving endpoints REJECT (400) a body
    // that is missing a field, and both are worked from here, so the fields are
    // collected in a real modal — window.prompt cannot collect two of them and
    // cannot make one conditional on another.
    const [form, setForm] = useState(null);

    if (!recon) return null;

    const buckets = ORPHAN_BUCKETS
        .map((b) => ({ ...b, rows: Array.isArray(recon[b.key]) ? recon[b.key] : [] }))
        .filter((b) => b.rows.length > 0);

    // "We could not finish checking the gateway" is NOT "all clear". The
    // backend probes torn-down checkouts with a 15s deadline and tells us
    // whether it got through them all; an empty stranded-captures list under a
    // partial probe means unknown, and it has to say so or a slow gateway reads
    // exactly like a clean ledger.
    const probePartial = recon.stranded_captures_partial === true;
    const probed = Number(recon.stranded_captures_probed);
    const probeTotal = Number(recon.stranded_captures_total);
    const probeCounts = Number.isFinite(probed) && Number.isFinite(probeTotal);
    // A disabled gateway means NO torn-down checkout was checked. That is only
    // worth saying (and only worth forcing the panel open for) when there were
    // candidates to check — otherwise there is nothing it could have hidden.
    const gatewayBlind = recon.gateway_mode === 'disabled'
        && Number.isFinite(probeTotal) && probeTotal > 0;

    // Hide only when there is genuinely nothing to WORK. A non-empty list is
    // never hidden, whatever else is missing from the payload — and "we could
    // not check" is itself something to show, never an empty panel. The
    // status summary alone (paid/failed/expired counts) no longer keeps the
    // panel open: it lives on the overview cards now (owner, 2026-09-07).
    if (!reconPanelVisible(recon)) return null;

    const rowKey = (bucketKey, row, idx) =>
        `${bucketKey}:${row.id ?? row.gateway_order_ref ?? row.deposit_id ?? idx}`;

    const run = async (key, { url, body, confirmText, successText }) => {
        if (busyKey) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmText)) return;
        setBusyKey(key);
        try {
            const res = await axios.post(url, body || {});
            const text = res.data?.message || successText;
            setOutcomes((o) => ({ ...o, [key]: { ok: true, text } }));
            toast.success(text);
            if (onResolved) await onResolved();
        } catch (err) {
            const text = err.response?.data?.error
                || err.response?.data?.message
                || 'The action failed. Nothing was changed — check the server log.';
            setOutcomes((o) => ({ ...o, [key]: { ok: false, text } }));
            toast.error(text);
        } finally {
            setBusyKey(null);
        }
    };

    const submitForm = async () => {
        if (!form) return;
        const note = String(form.note || '').trim();
        if (!note) return; // the button is disabled without one; belt and braces

        if (form.kind === 'mark_refunded') {
            const refundReference = String(form.refund_reference || '').trim();
            if (!refundReference) return;
            const ref = encodeURIComponent(String(form.row.gateway_order_ref || ''));
            setForm(null);
            await run(form.key, {
                url: `/api/payments/admin/checkouts/${ref}/mark-refunded`,
                body: { note, refund_reference: refundReference },
                confirmText:
                    `Record the capture on ${form.row.gateway_order_ref} as refunded at the gateway `
                    + `(reference ${refundReference})? This does NOT move money — refund it at the gateway first.`,
                successText: 'Recorded as refunded.',
            });
            return;
        }

        const resolution = String(form.resolution || '');
        const zohoPaymentId = String(form.zoho_payment_id || '').trim();
        if (!RESOLUTIONS.some((r) => r.value === resolution)) return;
        if (resolution === 'zoho_applied_manually' && !zohoPaymentId) return;
        const body = { resolution, note };
        if (zohoPaymentId) body.zoho_payment_id = zohoPaymentId;
        setForm(null);
        await run(form.key, {
            url: `/api/payments/admin/settlements/${form.row.id}/resolve`,
            body,
            confirmText:
                `Close settlement #${form.row.id} (${money(form.row.zoho_amount)}) as `
                + `"${RESOLUTIONS.find((r) => r.value === resolution).label}"? `
                + `${resolution === 'zoho_applied_manually'
                    ? 'It will read as SETTLED everywhere.'
                    : 'It will read as WRITTEN OFF — the invoices stay unpaid.'}`,
            successText: 'Settlement resolved.',
        });
    };

    const renderActions = (bucket, row, key) => {
        if (!canAct || !bucket.action) return null;
        const busy = busyKey === key;

        // Orphans and stranded captures are both gateway_checkouts rows, so the
        // one endpoint that exists for them is mark-refunded — the operator
        // refunds at the gateway, then records the reference here so the row
        // leaves the queue with an audit trail. There is no re-credit endpoint.
        if (bucket.action === 'checkout') {
            if (!row.gateway_order_ref) return null;
            return (
                <button
                    className="swallet__btn"
                    disabled={busy}
                    onClick={() => setForm({
                        kind: 'mark_refunded', key, row,
                        note: '', refund_reference: '',
                    })}
                >
                    {busy ? 'Working…' : 'Record gateway refund'}
                </button>
            );
        }

        // Unapplied settlements: retry the apply, or close the row out with an
        // explicit resolution when a human dealt with it outside the system.
        const status = String(row.status || '').toLowerCase();
        return (
            <>
                {['failed', 'applying'].includes(status) && row.id != null && (
                    <button
                        className="swallet__btn swallet__btn--success"
                        disabled={busy}
                        onClick={() => run(key, {
                            url: `/api/payments/admin/settlements/${row.id}/retry`,
                            confirmText: `Retry settlement #${row.id} (${money(row.zoho_amount)})? Idempotent — an already-applied settlement will not be applied twice.`,
                            successText: 'Settlement retried.',
                        })}
                    >
                        {busy ? 'Working…' : 'Retry settlement'}
                    </button>
                )}
                {row.id != null && (
                    <button
                        className="swallet__btn"
                        disabled={busy}
                        onClick={() => setForm({
                            kind: 'resolve', key, row,
                            resolution: row.zoho_payment_id ? 'zoho_applied_manually' : '',
                            zoho_payment_id: row.zoho_payment_id || '',
                            note: '',
                        })}
                    >
                        {busy ? 'Working…' : 'Resolve manually'}
                    </button>
                )}
            </>
        );
    };

    const formValid = form && String(form.note || '').trim() && (
        form.kind === 'mark_refunded'
            ? !!String(form.refund_reference || '').trim()
            : (RESOLUTIONS.some((r) => r.value === form.resolution)
                && (form.resolution !== 'zoho_applied_manually' || !!String(form.zoho_payment_id || '').trim()))
    );

    return (
        <div className="swallet__recon">
            <div className="swallet__reconHead">
                <h3 className="swallet__reconTitle">Reconciliation (gateway vs ledger)</h3>
                <span className="swallet__sectionSub">
                    Last {recon.days || 7} days. Every row below is money that may have left a client&apos;s card
                    without reaching the ledger — work them top to bottom.
                </span>
            </div>

            {probePartial && (
                <div className="swallet__reconWarn">
                    <strong>Gateway check incomplete.</strong>{' '}
                    {probeCounts
                        ? `Only ${probed} of ${probeTotal} torn-down checkouts could be checked against the gateway before the deadline.`
                        : 'Not every torn-down checkout could be checked against the gateway before the deadline.'}
                    {' '}The stranded-captures list below is therefore <em>incomplete</em> — an empty list here does
                    NOT mean no money is stranded. Reload in a few minutes; if it stays partial, the gateway is slow
                    or unreachable and the captures must be checked at the gateway directly.
                </div>
            )}
            {!probePartial && !gatewayBlind && probeCounts && probeTotal > 0 && (
                <div className="swallet__reconProbeOk">
                    All {probeTotal} torn-down checkout(s) in this window were checked against the gateway.
                </div>
            )}
            {gatewayBlind && (
                <div className="swallet__reconWarn">
                    <strong>The gateway is disabled in this environment.</strong> None of the {probeTotal} torn-down
                    checkout(s) in this window were checked against it, so stranded captures cannot be detected here
                    at all — an empty list below means nothing.
                </div>
            )}

            {buckets.map((bucket) => (
                <div className="swallet__reconOrphans" key={bucket.key}>
                    <div className="swallet__reconOrphanTitle">
                        {bucket.label} · {bucket.rows.length}
                    </div>
                    <div className="swallet__sectionSub" style={{ display: 'block', marginBottom: 6 }}>
                        {bucket.blurb}
                    </div>
                    <div className="swallet__reconOrphanList">
                        {bucket.rows.slice(0, 25).map((row, idx) => {
                            const key = rowKey(bucket.key, row, idx);
                            const outcome = outcomes[key];
                            return (
                                <div className="swallet__reconOrphanRow" key={key}>
                                    {row.gateway_order_ref && (
                                        <span className="swallet__reconOrphanRef">{row.gateway_order_ref}</span>
                                    )}
                                    {row.id != null && bucket.key !== 'orphans' && <span>#{row.id}</span>}
                                    {row.deposit_id != null && <span>Deposit #{row.deposit_id}</span>}
                                    {row.client_id != null && (
                                        <Link to={`/wallet/clients/${row.client_id}`} className="swallet__proofLink">
                                            {row.client_name || `Client #${row.client_id}`}
                                        </Link>
                                    )}
                                    {/* Direct-rail receipts carry the booking/request they
                                        were taken for and the rail they arrived on. Without
                                        these two the receipt buckets render as an amount and
                                        a reference with nothing to reconcile them against. */}
                                    {row.booking_id != null && (
                                        <Link to={`/bookings/${row.booking_id}`} className="swallet__proofLink">
                                            Booking #{row.booking_id}
                                        </Link>
                                    )}
                                    {row.request_id != null && <span>Request #{row.request_id}</span>}
                                    {row.rail && <span>{humanize(row.rail)} rail</span>}
                                    {row.kind && <span>{humanize(row.kind)}</span>}
                                    {row.source && <span>{humanize(row.source)}</span>}
                                    {row.amount != null && <span style={{ fontWeight: 900 }}>{money(row.amount)}</span>}
                                    {row.zoho_amount != null && (
                                        <span style={{ fontWeight: 900 }}>Zoho {money(row.zoho_amount)}</span>
                                    )}
                                    {row.applied_amount != null && <span>Applied {money(row.applied_amount)}</span>}
                                    {row.zoho_payment_id && (zohoOrgId ? (
                                        <a
                                            className="swallet__zohoLink"
                                            href={zohoPaymentUrl(zohoOrgId, row.zoho_payment_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open the payment receipt in Zoho Books"
                                        >
                                            Pmt {row.zoho_payment_id} ↗
                                        </a>
                                    ) : (
                                        <span className="swallet__reconOrphanRef">Pmt {row.zoho_payment_id}</span>
                                    ))}
                                    {row.status && (
                                        <span className={`swallet__chip ${statusChipClass(row.status)}`}>
                                            {row.status}
                                        </span>
                                    )}
                                    {row.deposit_status && (
                                        <span className={`swallet__chip ${statusChipClass(row.deposit_status)}`}>
                                            deposit: {row.deposit_status}
                                        </span>
                                    )}
                                    {row.gateway_state && (
                                        <span className="swallet__chip swallet__chip--red">
                                            gateway: {row.gateway_state}
                                        </span>
                                    )}
                                    {row.gateway_unknown_at && (
                                        <span className="swallet__chip swallet__chip--red">
                                            gateway outcome unknown since {formatDate(row.gateway_unknown_at)}
                                        </span>
                                    )}
                                    {row.notes && <span className="swallet__reconOrphanNote">{row.notes}</span>}
                                    {(row.created_at || row.resolved_at) && (
                                        <span className="swallet__reconOrphanDate">
                                            {formatDate(row.created_at || row.resolved_at)}
                                        </span>
                                    )}
                                    <span className="swallet__reconRowActions">
                                        {renderActions(bucket, row, key)}
                                    </span>
                                    {outcome && (
                                        <span
                                            className={`swallet__reconOutcome ${outcome.ok ? 'swallet__reconOutcome--ok' : 'swallet__reconOutcome--bad'}`}
                                        >
                                            {outcome.ok ? '✓ ' : '✗ '}{outcome.text}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {bucket.rows.length > 25 && (
                            <div className="swallet__reconOrphanMore">+{bucket.rows.length - 25} more…</div>
                        )}
                    </div>
                </div>
            ))}

            {form && (
                <div
                    className="swallet__overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) setForm(null); }}
                >
                    <div className="swallet__modal">
                        {form.kind === 'mark_refunded' ? (
                            <>
                                <h3 className="swallet__modalTitle">Record Gateway Refund</h3>
                                <p className="swallet__modalText">
                                    Order <strong>{form.row.gateway_order_ref}</strong>
                                    {form.row.amount != null && <> · <strong>{money(form.row.amount)}</strong></>}.
                                    This records that <em>you already refunded the capture at the gateway</em> so the
                                    row leaves this queue with an auditable reason. It does not move any money.
                                </p>
                                <div className="swallet__formGroup">
                                    <label className="swallet__formLabel">Gateway refund reference (required)</label>
                                    <input
                                        type="text"
                                        className="swallet__formInput"
                                        value={form.refund_reference}
                                        onChange={(e) => setForm((f) => ({ ...f, refund_reference: e.target.value }))}
                                        placeholder="The refund id shown by the gateway"
                                    />
                                </div>
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Note (required)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={form.note}
                                        onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                                        placeholder="What was refunded and why"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="swallet__modalTitle">Resolve Settlement</h3>
                                <p className="swallet__modalText">
                                    Settlement <strong>#{form.row.id}</strong>
                                    {form.row.zoho_amount != null && <> · Zoho <strong>{money(form.row.zoho_amount)}</strong></>}.
                                    Close it out because a human dealt with it outside the system.
                                </p>
                                <div className="swallet__formGroup">
                                    <label className="swallet__formLabel">Resolution (required)</label>
                                    <select
                                        className="swallet__formInput"
                                        value={form.resolution}
                                        onChange={(e) => setForm((f) => ({ ...f, resolution: e.target.value }))}
                                    >
                                        <option value="">Choose…</option>
                                        {RESOLUTIONS.map((r) => (
                                            <option key={r.value} value={r.value}>{r.label}</option>
                                        ))}
                                    </select>
                                    {RESOLUTIONS.filter((r) => r.value === form.resolution).map((r) => (
                                        <div className="swallet__sectionSub" key={r.value} style={{ display: 'block', marginTop: 4 }}>
                                            {r.hint}
                                        </div>
                                    ))}
                                </div>
                                {form.resolution === 'zoho_applied_manually' && (
                                    <div className="swallet__formGroup">
                                        <label className="swallet__formLabel">Zoho payment id (required)</label>
                                        <input
                                            type="text"
                                            className="swallet__formInput"
                                            value={form.zoho_payment_id}
                                            onChange={(e) => setForm((f) => ({ ...f, zoho_payment_id: e.target.value }))}
                                            placeholder="The payment id you created in Zoho"
                                        />
                                    </div>
                                )}
                                <div className="swallet__formGroup" style={{ marginBottom: 16 }}>
                                    <label className="swallet__formLabel">Note (required)</label>
                                    <textarea
                                        className="swallet__formTextarea"
                                        style={{ height: 60 }}
                                        value={form.note}
                                        onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                                        placeholder="Why this settlement is being closed this way"
                                    />
                                </div>
                            </>
                        )}
                        <div className="swallet__modalActions">
                            <button className="swallet__btn" onClick={() => setForm(null)}>Cancel</button>
                            <button
                                className="swallet__btn swallet__btn--primary"
                                onClick={submitForm}
                                disabled={!formValid}
                            >
                                {form.kind === 'mark_refunded' ? 'Record refund' : 'Resolve settlement'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
